/**
 * Auth state — **메모리 전용** (persist 제거).
 *
 * refresh-token-family-design 모델:
 *   - accessToken: 순수 메모리. 리로드 시 소멸 → 앱 init 의 콜드스타트 부트스트랩(/auth/refresh)으로 복구.
 *   - refreshToken: **웹 = FE 미보유**(HttpOnly 쿠키). **네이티브 = SecureStorage**(메모리 미러 + 기기 저장).
 *   - user: 메모리. 부트스트랩/Me 조회로 복구.
 *
 * 웹에서 setAuth/setTokens 의 refreshToken 인자는 무시된다(쿠키가 정본). 네이티브에서만 보관.
 */
import { create } from "zustand";
import { isNative } from "../native";
import { writeNativeRefresh } from "../api/refreshSecureStore";
import { reconcileLocalDataOwner } from "../lib/localDataOwner";

export interface AuthUser {
  memberSerialNo: number;
  email: string;
  /** 본명 — 픽쿠함 헤더 "○○ 님의 픽쿠함" 표시용. BE 미공급 시 undefined. */
  name?: string | null;
  nickname?: string | null;
  /** 자기소개 / Bio — GET /api/v1/user/members/me 또는 PUT 응답에서 채움. */
  introduction?: string | null;
  /** 프로필 사진 URL — Me 응답 또는 사진 업로드 응답에서 채움. */
  profileImageUrl?: string | null;
  /** OAuth provider — Google / Apple 2종 (2026-08-04 소셜 전용 재편). */
  provider?: "google" | "apple" | null;
}

interface AuthState {
  accessToken: string | null;
  /** 네이티브 전용 refresh 미러(헤더 전송용). 웹은 항상 null(쿠키). */
  refreshToken: string | null;
  user: AuthUser | null;
  /**
   * 콜드스타트 부트스트랩(/auth/refresh) settle 여부.
   * persist hydration(제거됨)을 대체 — deep-link replay / 푸시 기기 등록이 인증 복구 완료를 기다리는 신호.
   */
  bootstrapped: boolean;
  markBootstrapped: () => void;
  setAuth: (a: { accessToken: string; refreshToken: string; user: AuthUser }) => void;
  /** refresh 응답으로 받은 새 토큰만 갱신 (user 보존). silent refresh / 부트스트랩이 사용. */
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthUser) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
  /**
   * 로그아웃 — BE family 폐기(웹=쿠키 / 네이티브=헤더) + 로컬 clear.
   * BE 호출 실패해도 로컬 state 는 항상 clear (보안 우선).
   */
  logout: () => Promise<void>;
}

/**
 * 로그아웃 진행 래치 — logout() 시작~clear() 사이에 늦게 resolve 한 refresh 의 setTokens 가
 * 토큰을 되살리는(resurrection) TOCTOU 를 차단. 새 로그인(setAuth) 시 해제.
 */
let logoutInProgress = false;

/** 네이티브면 refresh 를 SecureStorage 에 미러링(웹은 쿠키라 무시). */
function persistNativeRefresh(refreshToken: string | null): string | null {
  if (!isNative()) return null;
  void writeNativeRefresh(refreshToken);
  return refreshToken;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  bootstrapped: false,
  markBootstrapped: () => set({ bootstrapped: true }),
  setAuth: ({ accessToken, refreshToken, user }) => {
    logoutInProgress = false; // 새 로그인 → 부활 가드 해제
    // 이 기기 로컬 데이터(최근 스캔·검색어)의 주인이 바뀌었으면 이전 계정분을 비운다.
    // clear() 는 토큰만 지우므로 여기서 하지 않으면 계정을 바꿔도 남의 기록이 보인다.
    reconcileLocalDataOwner(user.memberSerialNo);
    set({ accessToken, user, refreshToken: persistNativeRefresh(refreshToken) });
  },
  setTokens: (accessToken, refreshToken) => {
    // 로그아웃 진행 중 늦게 도착한 refresh → 무시(부활 방지).
    if (logoutInProgress) return;
    set({ accessToken, refreshToken: persistNativeRefresh(refreshToken) });
  },
  setUser: (user) => {
    // 콜드스타트 부트스트랩(RootLayout 의 /members/me)도 이 경로로 user 를 채운다 —
    // setAuth 를 안 거치는 복귀 흐름이라 여기서도 대조해야 빈틈이 없다.
    reconcileLocalDataOwner(user.memberSerialNo);
    set({ user });
  },
  clear: () => {
    persistNativeRefresh(null);
    set({ accessToken: null, refreshToken: null, user: null });
  },
  isAuthenticated: () => !!get().accessToken,
  logout: async () => {
    // 0) in-flight silent refresh 가 끝나길 먼저 기다린다 — 이건 정상 setTokens 가 되도록 가드 전.
    try {
      const { awaitPendingRefresh } = await import("../api/client");
      await awaitPendingRefresh();
    } catch {
      // ignore — 로그아웃 진행
    }
    // 부활 가드 ON — awaitPendingRefresh 이후~clear 사이(BE logout 비동기 윈도우)에 새로 시드돼
    // 늦게 resolve 하는 refresh 의 setTokens 를 무력화(TOCTOU 차단).
    logoutInProgress = true;
    // 0) 푸시 기기 해제 — 액세스 토큰이 아직 살아 있는 지금만 호출할 수 있다. 직발송은 기기
    //    토큰이 곧 타겟이라 종전 OneSignal alias 해제 같은 원격 정리가 없다 — 빠뜨리면
    //    로그아웃한 기기가 이전 회원의 푸시를 계속 받는다.
    try {
      const { unregisterPushDevice } = await import("../native/fcmPush");
      await unregisterPushDevice();
    } catch {
      // best-effort — 실패해도 로컬 clear 는 진행
    }
    // 1) BE family 폐기 (best-effort). 웹=쿠키 자동전송 / 네이티브=헤더.
    try {
      const { auth } = await import("../api/auth");
      if (isNative()) {
        const { readNativeRefresh } = await import("../api/refreshSecureStore");
        const token = await readNativeRefresh();
        await auth.logout(token ?? undefined);
      } else {
        await auth.logout();
      }
    } catch {
      // BE 호출 실패는 무시 — 로컬 clear 는 진행
    }
    // 2) Vendor SDK signOut (네이티브 전용, best-effort) — 다음 소셜 로그인에서 이전 계정이
    //    자동 재사용되지 않도록 벤더 세션을 정리한다. 웹은 각 wrapper 가 no-op
    //    (웹은 lib/socialAuth.ts authorize prompt 파라미터가 재인증을 담당).
    try {
      const provider = get().user?.provider ?? null;
      const { signOutSocialVendors } = await import("../native/socialAuth/signOut");
      await signOutSocialVendors(provider);
    } catch {
      // best-effort — 벤더 signOut 실패가 로컬 clear 를 막지 않는다.
    }
    // 3) 본인인증/소셜가입 흐름의 잔여 sessionStorage 상태 clear.
    //    안 비우면 하드 리로드(window.location.replace) 후에도 sessionStorage 가 살아남아
    //    stale redirect 가 /auth/identity/callback 로 잘못 이동 → "가입 실패(tempToken 누락)".
    try {
      const { clearAuthFlowState } = await import("../navigation/pendingAuthIntent");
      clearAuthFlowState();
    } catch {
      // best-effort — clear 실패해도 로컬 토큰 클리어는 진행.
    }
    // 4) GA user_id 해제 — 이후 이벤트가 이전 회원으로 귀속되지 않도록.
    try {
      const { resetUser } = await import("../lib/tracking");
      resetUser();
    } catch {
      // best-effort — 분석 해제 실패가 로그아웃을 막지 않는다.
    }
    // 5) 로컬 clear (+ 네이티브 SecureStorage 삭제)
    persistNativeRefresh(null);
    set({ accessToken: null, refreshToken: null, user: null });
  },
}));
